import { TTranslateFn } from '@kurtosys/types/common/index.js';
import { IDateSymbol, IFormatOptions, IFormatter } from '@kurtosys/types/data/index.js';
import { INumberThresholds } from '@kurtosys/types/data/INumberThresholds.js';
import { ITransform } from '@kurtosys/types/data/ITransform.js';
import { TFormatType } from '@kurtosys/types/data/TFormatType.js';

import { sortBy } from './collection.js';
import { formatDateWithStringFormat, getDateAsUTC, isValidDate } from './date.js';
import intlInstanceCache from './intlInstanceCache.js';
import { transform } from './transform.js';
import { isNullOrUndefined, isNumber } from './typeChecks.js';

export const formatNumber = (
	locale = 'en-GB',
	options: IFormatOptions,
	value: number,
	translate?: TTranslateFn,
): number | string => {
	if (isNumber(value)) {
		try {
			const { numberThresholds, thresholdSeparator = '', forceMinimumThresholdFormatting = false } = options;
			let numberThreshold: INumberThresholds | undefined;
			if (numberThresholds) {
				const sortedNumberThresholds = sortBy(numberThresholds, (item) => item.value.toString(), 'DESC');
				for (const numberGroup of sortedNumberThresholds) {
					const { value: thresholdValue } = numberGroup;
					const shouldFormat =
						sortedNumberThresholds.indexOf(numberGroup) === sortedNumberThresholds.length - 1 &&
						value < thresholdValue &&
						forceMinimumThresholdFormatting;
					if (Math.abs(value) >= Math.abs(thresholdValue) || shouldFormat) {
						numberThreshold = numberGroup;
						value = ((value < 0 ? -1 : 1) * Math.abs(value)) / Math.abs(thresholdValue);
						break;
					}
				}
			}
			const formattedNumber: number = intlInstanceCache.getNumber(locale, options).format(value);
			if (numberThreshold) {
				const { text, separator = thresholdSeparator, displayPosition = 'suffix' } = numberThreshold;
				const translatedText = (translate && translate(text)) || text;
				if (displayPosition === 'suffix') {
					return `${formattedNumber}${separator}${translatedText}`;
				}
				if (displayPosition === 'prefix') {
					return `${translatedText}${separator}${formattedNumber}`;
				}
			}
			return formattedNumber;
		} catch (e) {
			console.warn(e);
			return value;
		}
	}

	return value;
};

const getFormatSymbolData = (format: string, symbolChar: string): IDateSymbol => {
	const formatCharacters = format.split('');
	let start;
	let length = 0;

	for (let i = 0; i < formatCharacters.length; i++) {
		const char = formatCharacters[i];
		const prevChar = i > 0 ? formatCharacters[i - 1] : null;
		if (start === undefined) {
			if (char === symbolChar) {
				start = i;
				length++;
			}
		} else if (prevChar === symbolChar && char === symbolChar) {
			length++;
		}
	}

	return {
		start,
		length,
		key: format.slice(start, start + length),
	};
};

const getDatePart = (date: string, symbol: IDateSymbol, key: string, defaultPart?: string): string => {
	if (date && symbol && symbol.length > 0 && symbol.key === key) {
		return date.slice(symbol.start, symbol.start + symbol.length);
	}
	return defaultPart;
};

const formatGmtOffset = (hours: number): string => {
	if (hours === 0) {
		return 'GMT';
	}
	return `GMT${hours > 0 ? `+${hours}` : hours}`;
};

/**
 * This will take the defined timeZone or fallback to the user's timeZone to provide the GMT offset
 *
 * @param [targetDateStr] string
 * @param [targetTimeZone] string (defaults to user's timezone)
 * @returns string eg. GMT+2
 */
const getGmtOffset = (targetDateStr: string, targetTimeZone?: string): string => {
	// If we are targeting UTC, the offset will simply be GMT.
	if (targetTimeZone === 'UTC') {
		return 'GMT';
	}
	// If we are targeting another timezone, get the difference between UTC and the target time zone
	if (targetTimeZone) {
		const utcDate = getDateAsUTC(targetDateStr);
		if (!isValidDate(utcDate)) {
			return '';
		}
		// here we get timestamps from new date objects based on locale strings using the en-US culture which the Date constructor can read back.
		const utcTime = new Date(utcDate.toLocaleString('en-US', { timeZone: 'UTC' })).getTime();
		const tzTime = new Date(utcDate.toLocaleString('en-US', { timeZone: targetTimeZone })).getTime();
		// determine the hour offset between the two time zones.
		const diff = ((utcTime - tzTime) / 3600000) * -1;
		return formatGmtOffset(diff);
	}
	// Otherwise we are targeting the user's time zone to workout the time zone difference
	if (!targetTimeZone) {
		// Keeping DST in mind we create a date based on the date string
		const targetDate = new Date(targetDateStr);
		if (!isValidDate(targetDate)) {
			return '';
		}
		const diff = (targetDate.getTimezoneOffset() / 60) * -1;
		return formatGmtOffset(diff);
	}
};

interface IDateFromFormatProps {
	/**
	 * The date can be a date
	 */
	date: string | number;
	fromFormat: string;
	intlOptions: IFormatOptions;
}
const dateFromFormat = (props: IDateFromFormatProps): Date => {
	const { date, fromFormat, intlOptions } = props;
	let newDate: string | number = date;
	if (date && typeof date === 'string') {
		// determine what date parts can be pulled off of the provided date string.
		const usedSymbols = {
			year: getFormatSymbolData(fromFormat, 'Y'),
			month: getFormatSymbolData(fromFormat, 'M'),
			day: getFormatSymbolData(fromFormat, 'D'),
			hour: getFormatSymbolData(fromFormat, 'H'),
			minute: getFormatSymbolData(fromFormat, 'm'),
			second: getFormatSymbolData(fromFormat, 'S'),
		};

		// Get date part values based on from format.
		const dateTimeParts = {
			year: getDatePart(date, usedSymbols.year, 'YYYY'),
			month: getDatePart(date, usedSymbols.month, 'MM'),
			day: getDatePart(date, usedSymbols.day, 'DD'),
			hour: getDatePart(date, usedSymbols.hour, 'HH', '00'),
			minute: getDatePart(date, usedSymbols.minute, 'mm', '00'),
			second: getDatePart(date, usedSymbols.second, 'SS', '00'),
		};

		// build date & time strings
		const datePart = `${dateTimeParts.year}/${dateTimeParts.month}/${dateTimeParts.day}`;
		const timePart = `${dateTimeParts.hour}:${dateTimeParts.minute}:${dateTimeParts.second}`;
		const dateStrWithNoTimeZone = `${datePart} ${timePart}`;

		// determine time zone to use in order to provide GMT offset as a string.
		let appliedTimeZone;
		if (!intlOptions.dateFormatString && intlOptions.timeZone) {
			appliedTimeZone = intlOptions.timeZone;
		}
		const zonePart = `${getGmtOffset(dateStrWithNoTimeZone, appliedTimeZone)}`;

		// return date string that lines up with the target timezone so that date formatting does not shift out by a day.
		newDate = `${dateStrWithNoTimeZone} ${zonePart}`;
	}

	return new Date(newDate);
};

/**
 * Format a provided date string based on either a string format or using intl.DateTimeFormat.
 *
 * Note: it is important that we create a date object based on the time zone we are going to format in
 * (This prevents the output date shifting by a day, because of the different between the user time zone and the target timezone).
 *
 * @param [locale='en-GB']
 * @param options
 * @param value
 * @param [translate]
 * @returns
 */
export const formatDateTime = (
	locale = 'en-GB',
	options: IFormatOptions,
	value: string | number,
	translate?: TTranslateFn,
): string | number => {
	const fromDateFormat = options.fromDateFormat || 'YYYY-MM-DD';
	// in order to prevent the Intl formatter changing the date based on the users timezone, we can default to UTC here.
	if (!options.timeZone) {
		options.timeZone = 'UTC';
	}

	const date = dateFromFormat({
		date: value,
		fromFormat: fromDateFormat,
		intlOptions: options,
	});

	if (isValidDate(date)) {
		try {
			const { dateFormatString } = options;
			if (dateFormatString) {
				return formatDateWithStringFormat(date, dateFormatString, translate);
			}
			return intlInstanceCache.getDateTime(locale, options as Intl.DateTimeFormatOptions).format(date);
		} catch (e) {
			console.warn(e);
			return value;
		}
	}

	return value;
};

export const formatter = (format: IFormatter, value: number | string, translate?: TTranslateFn): string | number => {
	const { type, locale, currency, options, transforms } = format;
	return spreadFormatter(type, locale, currency, options, value, transforms, translate);
};

export const spreadFormatter = (
	type: TFormatType,
	locale = 'en-GB',
	currency: string | undefined,
	options: IFormatOptions = {},
	value: number | string,
	transforms?: ITransform[],
	translate?: TTranslateFn,
): string | number => {
	let response = value;
	const { placeholderText, placeholderKey = 'value' } = options;
	if (transforms && isNumber(value)) {
		const typesAllowedToTransform: TFormatType[] = ['number', 'percent', 'currency'];
		if (typesAllowedToTransform.includes(type)) {
			value = transform(value, transforms);
		}
	}

	if (type === 'string') {
		response = !isNullOrUndefined(value) && value.toString();
	}

	if (type === 'number') {
		response = formatNumber(locale, options, value as number, translate);
	}

	if (type === 'percent') {
		const workingOptions = Object.assign({}, options || {}, { style: 'percent' });
		response = formatNumber(locale, workingOptions, value as number, translate);
	}

	if (type === 'currency') {
		const workingOptions = Object.assign({}, options || {}, { style: 'currency' });
		workingOptions.currency = currency;
		response = formatNumber(locale, workingOptions, value as number, translate);
	}

	if (type === 'date') {
		response = formatDateTime(locale, options, value as string, translate);
	}

	if (translate && placeholderText) {
		response = translate(placeholderText, { [placeholderKey]: response });
	}

	return response;
};

/**
 * Converts the size from bytes to KB, MB, GB, TB, PB, EB, ZB, YB
 * @param {number} bytes Provides file size in bytes format
 * @param {number} [decimalPoint=0] Specify the decimal point. Defaults to 2
 * @returns string
 * @example
 * formatFileSize(2000);       // 2 KB
 * formatFileSize(2234);       // 2.23 KB
 * formatFileSize(2234, 3);    // 2.234 KB
 */
export const formatFileSize = (bytes: number, decimalPoint = 2): string => {
	if (bytes == 0) return '0 Bytes';
	const k = 1000;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return parseFloat((bytes / Math.pow(k, i)).toFixed(decimalPoint)) + ' ' + sizes[i];
};
